import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth-store';
import { subscriptionApi } from '../api/subscription-api';
import { tierCovers, type CurrentTenant, type SubscriptionTier } from '../types';

/**
 * Fetches the current tenant once per session and caches it (5 min staleTime).
 * Auto-disabled when logged out; nothing fires if `token` is null.
 *
 * Used by:
 * - <SubscriptionGate> to render upgrade prompts instead of locked features
 * - AppLayout's menu builder to hide Pro-only items from Starter tenants
 * - Quota meters on order entry / receipt OCR pages
 * - The /settings/subscription page itself
 */
export function useCurrentTenant() {
    const token = useAuthStore((s) => s.token);
    return useQuery<CurrentTenant>({
        queryKey: ['current-tenant'],
        queryFn: subscriptionApi.me,
        enabled: !!token,
        staleTime: 5 * 60 * 1000,        // 5 min — tier changes rare; refetch on tier-related events
        refetchOnWindowFocus: false,
    });
}

/**
 * Convenience: does the current tenant meet `required` tier?
 *
 * Fail-closed behaviour:
 * - While the initial /tenants/me fetch is in flight → `loading: true`, `covered: false`
 *   (callers should render a skeleton, not the locked UI).
 * - After fetch error → `loading: false`, `covered: false`. A Pro tenant whose
 *   /tenants/me call transiently failed will see "Upgrade to Pro" until they
 *   refresh. Trade-off accepted: failing open would let Starter see Pro
 *   features for the duration of a backend hiccup; failing closed is safer.
 *   The backend gate is still the source of truth — the actual API call
 *   wouldn't be allowed anyway.
 */
export function useTierCheck(required: SubscriptionTier): { covered: boolean; loading: boolean; currentTier?: SubscriptionTier } {
    const { data, isLoading } = useCurrentTenant();
    return {
        covered: tierCovers(data?.subscriptionTier, required),
        loading: isLoading,
        currentTier: data?.subscriptionTier,
    };
}
